import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Mention from '@tiptap/extension-mention';
import Table from '@tiptap/extension-table';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import { Placeholder } from '@tiptap/extensions'
import { ListKeymap } from '@tiptap/extension-list';
import HardBreak from '@tiptap/extension-hard-break';
import { useEditor, EditorContent, ReactRenderer } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { TextStyleKit } from '@tiptap/extension-text-style'
import { notification } from 'antd';
import { isEmpty } from 'lodash';
import { useEffect, useMemo, useRef } from 'react';
import tippy from 'tippy.js';

import CustomBulletedListExtension from './custom-extensions/CustomBulletedList/CustomBulletedListExtension';
import CustomHardBreakExtension from './custom-extensions/CustomHardBreak/CustomHardBreakExtension';
import CustomListItemExtension from './custom-extensions/CustomListItem/CustomListItemExtension';
import CustomOrderedListExtension from './custom-extensions/CustomOrderedList/CustomOrderedListExtension';
import InlineImageExtension from './custom-extensions/InlineImage/InlineImageExtension';
import OnSubmitExtension from './custom-extensions/OnSubmit/OnSubmitExtension'; 
import { PasteConverter } from './custom-extensions/PasteConverter/PasteConverterPlugin';
import SlashMenuExtension from './custom-extensions/slashMenu/SlashMenuExtension';
import TrailingLineExtension from './custom-extensions/TrailingLine/TrailingLineExtension';

import { processInternalLinks } from './utils/editorContentOperations';
import BubbleToolbar from './BubbleToolbar';
import LinkEditorComponent from './LinkEditorComponent';
import { MentionList } from './MentionList';
import { createSlashMenuSuggestion } from './SlashMenu';
import SlashMenuHintComponent from './SlashMenuHintComponent';
import TextEditorToolbar from './TextEditorToolbar';
import './style.css';

const createMentionSuggestion = (mentionOptions) => ({
  items: ({ query }) => {
    const currentOptions = typeof mentionOptions === 'function' ? mentionOptions() : mentionOptions;
    const userOptions = Array.isArray(currentOptions) ? currentOptions : [];

    if (userOptions.length === 0) {
      return [];
    }

    if (!query || query.length === 0) {
      return userOptions.slice(0, 10);
    }

    return userOptions.filter((item) => item.value.toLowerCase().includes(query.toLowerCase())).slice(0, 10);
  },

  render: () => {
    let component;
    let popup;
    let isInitialized = false;

    return {
      onStart: (props) => {
        const currentOptions = typeof mentionOptions === 'function' ? mentionOptions() : mentionOptions;
        const userOptions = Array.isArray(currentOptions) ? currentOptions : [];

        component = new ReactRenderer(MentionList, {
          props,
          editor: props.editor,
        });

        if (!props.clientRect) {
          return;
        }

        if (userOptions.length > 0) {
          popup = tippy('body', {
            getReferenceClientRect: props.clientRect,
            appendTo: () => document.body,
            content: component.element,
            showOnCreate: true,
            interactive: true,
            trigger: 'manual',
            placement: 'bottom-start',
            zIndex: 9999,
          });
        }

        isInitialized = true;
      },

      onUpdate(props) {
        const currentOptions = typeof mentionOptions === 'function' ? mentionOptions() : mentionOptions;
        const userOptions = Array.isArray(currentOptions) ? currentOptions : [];

        if (!component && isInitialized) {
          component = new ReactRenderer(MentionList, {
            props,
            editor: props.editor,
          });
        }

        if (userOptions.length > 0 && !popup && props.clientRect && component) {
          popup = tippy('body', {
            getReferenceClientRect: props.clientRect,
            appendTo: () => document.body,
            content: component.element,
            showOnCreate: true,
            interactive: true,
            trigger: 'manual',
            placement: 'bottom-start',
            zIndex: 9999,
          });
        }

        if (userOptions.length === 0 && popup) {
          const popupInstance = Array.isArray(popup) ? popup[0] : popup;
          if (popupInstance && popupInstance.hide) {
            popupInstance.hide();
          }
        }

        if (userOptions.length > 0 && popup) {
          const popupInstance = Array.isArray(popup) ? popup[0] : popup;
          if (popupInstance && popupInstance.show) {
            popupInstance.show();
          }
        }

        if (component) {
          component.updateProps(props);
        }

        if (!props.clientRect || !popup) {
          return;
        }

        const popupInstance = Array.isArray(popup) ? popup[0] : popup;
        if (popupInstance && popupInstance.setProps) {
          popupInstance.setProps({
            getReferenceClientRect: props.clientRect,
          });
        }
      },

      onKeyDown(props) {
        const currentOptions = typeof mentionOptions === 'function' ? mentionOptions() : mentionOptions;
        const userOptions = Array.isArray(currentOptions) ? currentOptions : [];

        if (props.event.key === 'Escape') {
          const popupInstance = Array.isArray(popup) ? popup[0] : popup;
          if (popupInstance && popupInstance.hide) {
            popupInstance.hide();
          }
          return true;
        }

        if (userOptions.length === 0) {
          return false;
        }

        if (component && component.ref && typeof component.ref.onKeyDown === 'function') {
          return component.ref.onKeyDown(props);
        }

        return false;
      },

      onExit() {
        if (popup) {
          const popupInstance = Array.isArray(popup) ? popup[0] : popup;
          if (popupInstance && popupInstance.destroy) {
            popupInstance.destroy();
          }
          popup = null;
        }
        if (component && component.destroy) {
          component.destroy();
          component = null;
        }
        isInitialized = false;
      },
    };
  },
});

const TextEditor = ({
  entityId,
  entityType,
  onChange,
  onSubmit,
  mentionOptions = [],
  placeholder = 'Start typing...',
  large,
  editorContent,
  handleBlur,
  showMentionOptions = true,
  textEditorRef
}) => {
  const mentionOptionsRef = useRef(mentionOptions);
  const isMentionDisabled = useMemo(() => isEmpty(mentionOptions), [mentionOptions]);
  const linkEditorComponentId = useMemo(() => Math.ceil(Math.random() * 1000000000), []);

  useEffect(() => {
    mentionOptionsRef.current = mentionOptions;
  }, [mentionOptions]);

  // const compressImage = (file, maxWidth = 800, maxHeight = 600, quality = 0.8) => {
  //   return new Promise((resolve, reject) => {
  //     const canvas = document.createElement('canvas');
  //     const ctx = canvas.getContext('2d');
  //     const img = document.createElement('img');

  //     img.onload = () => {
  //       let { width, height } = img;

  //       if (width > maxWidth || height > maxHeight) {
  //         const ratio = Math.min(maxWidth / width, maxHeight / height);
  //         width *= ratio;
  //         height *= ratio;
  //       }

  //       canvas.width = width;
  //       canvas.height = height;
  //       ctx.drawImage(img, 0, 0, width, height);

  //       URL.revokeObjectURL(img.src);
  //       resolve(canvas.toDataURL('image/jpeg', quality));
  //     };

  //     img.onerror = () => {
  //       URL.revokeObjectURL(img.src);
  //       reject(new Error('Failed to load image'));
  //     };

  //     img.src = URL.createObjectURL(file);
  //   });
  // };

  const handleImagePaste = async (event) => {
    if (!entityId || !entityType) return;

    const items = Array.from(event.clipboardData?.items || []);

    for (const item of items) {
      if (!item.type.includes('image')) continue;

      event.preventDefault();
      event.stopImmediatePropagation();

      const blob = item.getAsFile();
      if (!blob) continue;

      try {
        editor.chain().focus().setCustomImage({ file: blob, entityId, entityType }).run();
      } catch (error) {
        notification.error({
          message: 'Image Upload Failed',
          description: 'There was an error processing your image. Please try again or use a different image.',
          duration: 4,
        });
      }
    }
  };

  const editor = useEditor({
    // Order of extensions here is important
    extensions: [
      TextStyleKit,
      Placeholder.configure({
        placeholder,
      }),
      StarterKit.configure({ link: false, listItem: false, orderedList: false, bulletList: false, hardBreak: false }),
      CustomBulletedListExtension.configure({ keepAttributes: true }),
      CustomOrderedListExtension.configure({ keepAttributes: true }),
      CustomListItemExtension,
      // CustomHardBreakExtension,
      HardBreak,
      ListKeymap,
      Underline,
      InlineImageExtension,
      SlashMenuExtension.configure({
        suggestion: createSlashMenuSuggestion(entityId, entityType, linkEditorComponentId, showMentionOptions),
      }),
      Link.configure({
        openOnClick: true,
        autolink: true,
        linkOnPaste: true,
        protocols: ['http', 'https', 'tel', 'mailto'],
        validate: (href) => /^https?:\/\//.test(href) || /^www\./.test(href) || /^tel:/.test(href),
        HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
      }),
      Image.configure({
        inline: false,
        allowBase64: true,
        HTMLAttributes: {
          style: 'max-width: 100%; height: auto; border-radius: 4px; margin: 4px 0;',
        },
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
        alignments: ['left', 'center', 'right', 'justify'],
        defaultAlignment: 'left',
      }),
      Mention.configure({
        HTMLAttributes: {
          class: 'mention',
          style: 'color: #007bff; padding: 2px 4px; border-radius: 3px; text-decoration: none;',
        },
        suggestion: createMentionSuggestion(() => mentionOptionsRef.current),
      }),
      Table.configure({
        resizable: true,
        allowTableNodeSelection: true,
        draggable: false,
        HTMLAttributes: {
          class: 'editor-table',
        },
      }),
      TableRow.configure({ HTMLAttributes: { class: 'editor-table-row' } }),
      TableHeader.configure({
        HTMLAttributes: {
          class: 'editor-table-header',
        },
      }),
      TableCell.configure({
        HTMLAttributes: {
          class: 'editor-table-cell',
          draggable: false,
        },
      }),
      TrailingLineExtension,
      PasteConverter,
      onSubmit && OnSubmitExtension.configure({ onSubmit }),
    ],
    content: editorContent || '',
    onUpdate: ({ editor }) => {
      const rawHtml = editor.getHTML();
      const trimmedTextContent = editor.getText().trim();
      const processedHtml = processInternalLinks(rawHtml);
      onChange?.(processedHtml, trimmedTextContent);
    },
    onBlur: () => handleBlur?.(),
    onCreate: ({ editor }) => {
      editor.view.dom.addEventListener('input', (event) => {
        if (editor.isActive('link') && event.inputType === 'insertText') {
          setTimeout(() => editor.chain().focus().unsetLink().run(), 0);
        }
      });
      editor.view.dom.addEventListener('paste', handleImagePaste, true);
    },
  });
  
  useEffect(() => {
    if (textEditorRef) {
      textEditorRef.current = editor;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  useEffect(() => {
    console.info(`[TextEditor]: loaded editor with entityId: ${entityId}, entityType: ${entityType}`);
  }, [entityId, entityType]);

  useEffect(() => {
    if (editor) {
      console.info('[TextEditor]: updatesubmitfn triggered');
      editor?.commands?.updatesubmitfn?.(onSubmit);
    }
  }, [editor, onSubmit]);

  useEffect(() => {
    if (editor && editorContent !== editor.getHTML()) {
      editor.commands.setContent(editorContent || '');
    }
  }, [editorContent, editor]);

  if (!editor) return null;

  return (
    <div className={`tiptap-editor ${large ? 'large-editor' : ''}`} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div className="overflow-y-auto" style={{ maxHeight: large ? '100%' : '120px', flex: '1 1 auto', minHeight: 0, padding: '6px 8px' }}>
        <BubbleToolbar editor={editor} />
        <SlashMenuHintComponent editor={editor} />
        <LinkEditorComponent editor={editor} componentId={linkEditorComponentId} />
        <EditorContent editor={editor} placeholder={placeholder} />
      </div>
      <TextEditorToolbar
        entityId={entityId}
        entityType={entityType}
        editor={editor}
        linkEditorComponentId={linkEditorComponentId}
        showMentionOptions={!isMentionDisabled}
        showImageOption={!!(entityType && entityId)}
      />
    </div>
  );
};

export default TextEditor;
